import { createContext, useContext, useState, useEffect } from 'react';
import { tenantsApi } from '../lib/api';
import type { ClientTenant } from '../types';

interface TenantContextValue {
  tenants: ClientTenant[];
  selectedTenantId: string | undefined;
  selectedTenant: ClientTenant | undefined;
  setSelectedTenantId: (id: string) => void;
}

const TenantContext = createContext<TenantContextValue>({
  tenants: [],
  selectedTenantId: undefined,
  selectedTenant: undefined,
  setSelectedTenantId: () => {},
});

export function useTenantContext() {
  return useContext(TenantContext);
}

export function TenantProvider({ children }: { children: React.ReactNode }) {
  const [tenants, setTenants] = useState<ClientTenant[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string | undefined>(undefined);

  useEffect(() => {
    // Use raw fetch (not the api wrapper) so a 401 is handled silently here
    // instead of triggering window.location.href = '/login' in the request() wrapper,
    // which would cause an infinite redirect loop on the login page.
    // BASE_URL is prepended inline so UAT/Prod builds hit the API subdomain.
    const apiBase = import.meta.env.VITE_API_BASE_URL ?? '';
    fetch(`${apiBase}/api/auth/me`, { credentials: 'include' })
      .then((res) => {
        if (!res.ok) return null;          // 401 or any error → do nothing
        return res.json();
      })
      .then((user) => {
        if (!user || user.role !== 'super_admin') return;
        return tenantsApi.list().then((list) => {
          setTenants(list);
          const first = list.find((t) => t.slug !== 'spendlens') ?? list[0];
          if (first) setSelectedTenantId(first.id);
        });
      })
      .catch(() => {});
  }, []);

  const selectedTenant = tenants.find((t) => t.id === selectedTenantId);

  return (
    <TenantContext.Provider value={{ tenants, selectedTenantId, selectedTenant, setSelectedTenantId }}>
      {children}
    </TenantContext.Provider>
  );
}
